'use client';
import Typography from '@/components/shared/typography/typography.component';
import { createRef, useEffect, useMemo, useRef, useState } from 'react';

import Link from '@/components/WrappedLink';
import {
    Ar200,
    Ar7,
    Ar90,
    ArPercentage,
    En200,
    En7,
    En90,
    EnPercentage,
    LeftArrow,
    RightArrow,
} from '../../../../public/Images';
import { useTranslation, useWindowDimensions } from '@/hook';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import colors from '@/theme/colors.module.scss';
import Conditional from '@/components/shared/conditional/conditional-client.component';

const ValuePropositionSlider = ({
    bannerWidth,
    bannerHeight,
}: {
    bannerWidth: number;
    bannerHeight: number;
}) => {
    const ArImgs = [ArPercentage, Ar7, Ar90, Ar200];
    const EnImgs = [EnPercentage, En7, En90, En200];

    const [activeIndex, setActiveIndex] = useState(0);
    const [refs, setRefs] = useState(useRef(null));
    const { i18n, t, IS_RTL } = useTranslation();
    const { IS_DESKTOP = false } = useWindowDimensions();
    const isMobile = !IS_DESKTOP;
    const imgs = IS_RTL ? ArImgs : EnImgs;
    const mainRef = useRef<HTMLDivElement>(null);
    const path = usePathname();
    const inCarListing = path.includes('buy-used-cars');
    const inCarProfile = path.includes('car-details');

    const arrowsDimentions = useMemo(
        () => (isMobile ? '36px' : '44px'),
        [isMobile]
    );
    useEffect(() => {
        const thumbnailsRefs: any = [];

        imgs?.map(() => {
            return thumbnailsRefs.push(createRef());
        });

        const newRef = createRef();

        // @ts-ignore
        newRef.current = thumbnailsRefs;

        // @ts-ignore

        setRefs(newRef);
    }, []);

    const handleImgClicked = (index: number) => {
        setActiveIndex(index);
    };

    const onArrowClicked = (isRight: boolean) => {
        const nextIndex = isRight ? imgs.length - 1 : 0;
        if (nextIndex >= 0 && nextIndex < imgs?.length) {
            setActiveIndex(nextIndex);
            // @ts-ignore
            refs?.current?.[nextIndex]?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }
    };

    const getDimensions = (ele: HTMLDivElement) => {
        const offsetLeft = ele.offsetLeft;
        const offsetRight = offsetLeft + ele.offsetWidth;

        return {
            offsetLeft,
            offsetRight,
        };
    };

    const handleScroll = () => {
        if (!refs?.current || !mainRef.current) return;
        const margins = 8 * 2 * imgs.length;
        const scrollPosition = mainRef.current.scrollLeft + margins;
        // @ts-ignore
        refs?.current?.forEach((ref: { current: any }, index: number) => {
            // @ts-ignore
            const ele = ref.current;

            if (ele) {
                const { offsetLeft, offsetRight } = getDimensions(ele);
                if (IS_RTL) {
                    if (
                        scrollPosition > offsetRight &&
                        scrollPosition < offsetLeft
                    ) {
                        setActiveIndex(index);
                    }
                } else if (
                    scrollPosition > offsetLeft &&
                    scrollPosition < offsetRight
                ) {
                    setActiveIndex(index);
                }
            }
        });
    };
    return (
        <div
            className={
                'border-gray-200 py-6 border-t border-b border-solid lg:py-0 lg:border-0'
            }
        >
            <Conditional condition={(inCarListing && isMobile) || inCarProfile}>
                <>
                    <div className={'flex justify-between relative'}>
                        <Typography
                            variant={'subtitle1Bold'}
                            className={'text-gray-500'}
                        >
                            {t('Sylndr Certified')}
                        </Typography>
                        <Link
                            className={
                                'cursor-pointer no-underline flex gap-1 items-center'
                            }
                            href={`/${i18n.language}/why-sylndr`}
                        >
                            <Typography
                                style={{ marginTop: '2px' }}
                                variant={'captionMedium'}
                                className={'text-gray-500'}
                            >
                                {t('Why Sylndr')}
                            </Typography>
                            {IS_RTL ? (
                                <ChevronLeft sx={{ color: colors.gray500 }} />
                            ) : (
                                <ChevronRight sx={{ color: colors.gray500 }} />
                            )}
                        </Link>
                    </div>
                    <Typography variant={'caption'} className={'text-gray-500'}>
                        {t('Buy verified car')}
                    </Typography>
                </>
            </Conditional>
            <div
                className={
                    'scrollbar-hide overflow-x-auto overflow-y-hidden whitespace-nowrap gap-2 flex w-full justify-start mt-2 lg:mt-0'
                }
                onScroll={handleScroll}
                ref={mainRef}
            >
                {isMobile && !inCarProfile && (
                    <div
                        className={
                            'absolute w-[calc(100vw_-_32px)] right-auto top-[calc(50%_+_24px)]'
                        }
                    >
                        {activeIndex <= imgs.length / 2 - 2 && (
                            <img
                                src={RightArrow.src}
                                className={
                                    'absolute cursor-pointer mx-0 my-auto inset-y-0 -scale-x-100 left-1 en:right-1 en:left-[unset]'
                                }
                                onClick={() => onArrowClicked(true)}
                                width={arrowsDimentions}
                                height={arrowsDimentions}
                                alt={''}
                                fetchPriority={'low'}
                            />
                        )}
                        {activeIndex >= imgs.length / 2 - 1 && (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                                src={LeftArrow.src}
                                className={
                                    'absolute cursor-pointer mx-0 my-auto inset-y-0 right-1 -scale-x-100 en:right-[unset] en:left-1'
                                }
                                onClick={() => onArrowClicked(false)}
                                width={arrowsDimentions}
                                height={arrowsDimentions}
                                alt={''}
                                fetchPriority={'low'}
                            />
                        )}
                    </div>
                )}
                {imgs?.map((img, index) => {
                    return (
                        <Link
                            key={img.src + index}
                            className={`flex h-full  ${
                                inCarProfile ? ' mt-0 lg:mt-2 ' : ''
                            }`}
                            href={`/${i18n.language}/why-sylndr`}
                        >
                            <Image
                                width={bannerWidth}
                                height={bannerHeight}
                                src={img.src}
                                ref={refs?.current?.[index]}
                                alt={'value ' + index}
                                onClick={() => {
                                    handleImgClicked(index);
                                }}
                                className={
                                    'object-contain inline-block cursor-pointer box-border p-0 rounded-[4%] w-[45vw] max-w-[45vw] h-auto lg:w-full lg:max-w-full'
                                }
                            />
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

export default ValuePropositionSlider;
